import { Grid, useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useJobApplication from "../../../hooks/useJobApplication";
import {
  updateBulkApplyState,
  updatePostApplyDialog,
  updateShowSimilarJobsStatus,
} from "../../../redux/actions/dialog_action";
import {
  bulkApplyJobs,
  clearBulkAppliedJobs,
  clearTotalBulkAppliedJobs,
  getPostApplyRecommendedJobs,
  triggerJobReload,
  updateSelectedJob,
} from "../../../redux/actions/job_action";
import AppliedJobList from "./AppliedJobList/AppliedJobList";
import BulkApplyList from "./BulkApplyList/BulkApplyList";
import MobileAppAd from "./MobileAppAd/MobileAppAd";
import SecondaryBulkApply from "./SecondaryBulkApply/SecondaryBulkApply";
import {
  AppliedButtonStyled,
  AppliedJobContainer,
  AppliedJobWrapper,
  BoxContainer,
  ButtonStyled,
  CheckIcon,
  CloseButton,
  CloseIcon,
  ContentText,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  HeaderStyled,
  LinkStyled,
  TextStyled,
  WarnIcon,
} from "./styles";

export default function PostApplyDialog(props) {
  const {
    page,
    origin,
    handleTabChange,
    setDialogOpen,
    jobListRef,
    selectedTab,
  } = props;
  const isMobile = useMediaQuery("(max-width: 640px)");

  const router = useRouter();
  const dispatch = useDispatch();
  const posthog = usePostHog();

  let originUrl = "";

  if (typeof window !== "undefined") {
    originUrl = localStorage?.getItem("apply_origin") ?? "";
  }

  const [showList, setShowList] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const userResume = useSelector((state) => state?.user?.user?.state);

  const {
    status: showDialog,
    scraped: isScraped,
    jobGlobalId,
  } = useSelector((state) => state.dialog.showPostApplyDialog);

  const bulkApplyState = useSelector((state) => state?.dialog?.bulkApplyState);
  const applyingJobs = useSelector((state) => state?.jobs?.applyingJobs);
  const bulkAppliedJobs = useSelector((state) => state?.jobs?.bulkAppliedJobs);
  const bulkAppliedJobsTotal = useSelector(
    (state) => state?.jobs?.bulkAppliedJobsTotal
  );
  const jobReload = useSelector((state) => state?.jobs?.jobReload);
  const recommendedJobs = useSelector((state) => state?.jobs?.recommendedJobs);
  const fetchingRecommendedJobs = useSelector(
    (state) => state?.jobs.fetchingRecommendedJobs
  );
  const similarJobsTriggered = useSelector(
    (state) => state.dialog?.showSimilarJobs
  );

  // Memoize the selected jobs to only rerender on currentPage or recommendedJobs change
  const filteredJobs = useMemo(() => {
    if (!recommendedJobs) return [];
    const startIndex = currentPage * 6;
    return recommendedJobs?.slice(startIndex, startIndex + 6);
  }, [recommendedJobs, currentPage]);

  const jobOrigin = "job_list";
  const jobDiscovery = "search";
  const fullUrl = router?.asPath;

  const {
    checkLoggedIn,
    checkNationalityPhoneNumberLocation,
    checkRightToWorkAndRelocate,
    checkResumeRejected,
    checkScrapedJob,
  } = useJobApplication(jobOrigin, jobDiscovery);

  const selectedJobsLength = selectedJobs?.length ?? 0;
  const bulkApplyJobsStatus =
    bulkAppliedJobs?.length > 0 && bulkApplyState === "applied";

  const isLoadingJobs = fetchingRecommendedJobs || isLoading;

  const DialogHeaderIcon = useMemo(() => {
    if (!showDialog) return "";
    if ((isScraped && !bulkApplyJobsStatus) || userResume === "in_review")
      return <WarnIcon />;
    if (userResume === "approved") return <CheckIcon />;
    return <WarnIcon />;
  }, [isScraped, userResume, showDialog, bulkApplyJobsStatus]);

  const DialogHeaderTitle = useMemo(() => {
    if (!showDialog) return "";
    if (isScraped && !bulkApplyJobsStatus) return "Proceed in New Tab";
    if (userResume === "approved") return "Application successful";
    return "Application In Progress";
  }, [isScraped, userResume, showDialog, bulkApplyJobsStatus]);

  const DialogHeaderSubtitle = useMemo(() => {
    if (isScraped) {
      return "Continue your application in the new tab, in the meantime apply to other similar jobs!";
    }

    const redirectLink = (
      <LinkStyled
        onClick={handleViewJobApplicationHistory}
        textDecoration="underline"
      >
        Job Application Status
      </LinkStyled>
    );

    const subtitleCopy =
      userResume === "approved"
        ? "Your application will be submitted in 30 minutes; review or undo it in your "
        : "Your application will be submitted after resume approval; review or undo in ";

    return (
      <>
        {subtitleCopy}
        {redirectLink}
      </>
    );
  }, [isScraped, userResume]);

  const ApplyButtonText = `Apply to ${
    selectedJobsLength === 6 ? "All" : "Selected"
  } (${selectedJobsLength})`;

  const disableApplyButton = selectedJobsLength === 0 || applyingJobs;

  const user = useSelector((state) => state.user?.user);

  function handleCloseDialog() {
    setSelectedJobs([]);
    dispatch(clearTotalBulkAppliedJobs());
    dispatch(clearBulkAppliedJobs());
    dispatch(
      updatePostApplyDialog({
        status: false,
        scraped: false,
        jobGlobalId: null,
      })
    );
    if (similarJobsTriggered) {
      sendTrackingEvent({
        event: "CE_view_similar_jobs_back_click",
        origin: fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page",
        job_discovery: "view_similar_jobs",
        step: "view_similar_jobs_click",
      });
    }
    localStorage?.removeItem("scraped_job_id");
  }

  function handleCloseAndRedirect() {
    const dontShow = localStorage.getItem(
      `currentSalaryDialogDontShow_${user?.id}`
    );
    if (bulkApplyJobsStatus || page === "for-you") {
      dispatch(triggerJobReload(!jobReload));
    }
    setDialogOpen(true);
    handleCloseDialog();
    dispatch(updateShowSimilarJobsStatus(false));
    dispatch(updateBulkApplyState("post-apply"));

    if (!user?.currentSalary && !dontShow) {
      localStorage.setItem("showCurrentSalaryDialogAfterJump", "1");
    }
    if (router?.pathname?.includes("/apply")) {
      if (originUrl) {
        router?.push(originUrl);
        localStorage?.removeItem("apply_origin");
        localStorage?.removeItem("bulkApplyFromSimilarJob");
      } else {
        router?.push("/jobs");
      }
    }
  }

  function handleViewAllJobs() {
    const dontShow = localStorage.getItem(
      `currentSalaryDialogDontShow_${user?.id}`
    );
    if (page === "job-list") {
      return handleCloseAndRedirect();
    }

    if (!user?.currentSalary && !dontShow) {
      localStorage.setItem("showCurrentSalaryDialogAfterJump", "1");
    }
    handleCloseDialog();
    if (page === "for-you") {
      handleTabChange("job-list");
    } else {
      router.push("/jobs");
    }

    return router.push("/jobs");
  }

  async function handleApplyBulkJob(jobs) {
    const jobIds = jobs.map((job) => job.id);

    let applySourceNames =
      // Case 1: Profile - Application Applied
      fullUrl.includes("job-application-history") && origin === "Applied"
        ? similarJobsTriggered
          ? "profile_application_applied_bulk_apply_view_similar_jobs"
          : "profile_application_applied_bulk_apply_post_apply_rec"
        : // Case 2: Job Listing Page (JLP)
        selectedTab === "job-list"
        ? "jlp_bulk_apply_post_apply_rec"
        : // Case 3: For You Page (FYP)
        selectedTab === "for-you"
        ? "fyp_bulk_apply_post_apply_rec"
        : // Case 4: Profile - Application Recommended
        fullUrl.includes("job-application-history")
        ? "profile_application_recommend_bulk_apply_post_apply_rec"
        : // Case 5: Profile - Saved Jobs and Companies
        fullUrl.includes("saved-jobs-&-companies")
        ? selectedTab === "saved-jobs"
          ? "profile_saved_jobs_bulk_apply_post_apply_rec"
          : "profile_saved_recommend_bulk_apply_post_apply_rec"
        : // Case 6: Default - from Job Details Page (JDP)
        similarJobsTriggered
        ? "jdp_bulk_apply_view_similar_jobs"
        : "jdp_bulk_apply_post_apply_rec";

    const params = {
      jobIds: jobIds,
      applySource: applySourceNames,
    };

    // If multiple jobs are selected
    if (jobs.length > 1) {
      // NOT LOGGED IN
      if (checkLoggedIn(jobs, true)) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        // CE to track visitor click on continue button
        sendTrackingEvent({
          event: "CE_view_similar_jobs_continue_click",
          origin: fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page",
          job_discovery: "view_similar_jobs",
          is_visitor: true,
        });
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }
      if (
        // CONDITION - NO NATIONALITY OR MOBILE NUMBER OR CURRENT LOCATION
        checkNationalityPhoneNumberLocation(jobs, true) ||
        // CONDITION - NO RESUME / RESUME REJECTED
        checkResumeRejected(jobs, true)
      ) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }
    } else {
      if (checkLoggedIn(jobs[0])) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        sendTrackingEvent({
          event: "CE_view_similar_jobs_continue_click",
          origin: fullUrl === "/jobs" ? "job_listing_page" : "job_detail_page",
          job_discovery: "view_similar_jobs",
          is_visitor: true,
        });
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }

      if (
        checkNationalityPhoneNumberLocation(jobs[0]) ||
        // CONDITION - RIGHT TO WORK OR RELOCATION PREFERENCE
        checkRightToWorkAndRelocate(jobs[0]) ||
        checkResumeRejected(jobs[0]) ||
        // CONDITION - SCRAPED JOB
        checkScrapedJob(jobs[0])
      ) {
        localStorage.setItem("bulkApplyFromSimilarJob", "true");
        dispatch(updateShowSimilarJobsStatus(false));
        dispatch(updatePostApplyDialog({ status: false }));
        return;
      }
    }

    setDialogOpen(true);
    const res = await dispatch(bulkApplyJobs(params));

    if (res.success) {
      posthog.capture(
        //posthog tracking (Apply Jobs button) [JSW-3026]
        "job_bulk_apply",
        {
          from_job: jobGlobalId,
          job_discovery: "post_apply_rec",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        }
      );

      if (bulkApplyState === "secondary-bulk") {
        sendTrackingEvent({
          event: "CE_job_bulk_apply",
          from_job: jobGlobalId,
          job_discovery: "post_apply_rec",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        });

        // Dispatch applied state only if its on last job
        if (filteredJobs.at(-1)?.id === recommendedJobs.at(-1)?.id) {
          dispatch(updateBulkApplyState("applied"));
          return;
        }

        setIsLoading(true);
        setCurrentPage(currentPage + 1);

        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      } else {
        setIsLoading(true);

        const params = {
          globalId: jobGlobalId,
          first: 6,
        };

        //CE tracking (Bulk Apply Jobs button - registered users only) [JSW-3329]
        sendTrackingEvent({
          event: "CE_job_bulk_apply",
          from_job: jobGlobalId,
          job_discovery: "view_similar_jobs",
          num_jobs_apply: jobs.length,
          job_ids: jobIds,
        });

        dispatch(updateShowSimilarJobsStatus(false));
        // dispatch(getPostApplyRecommendedJobs(params));
        setCurrentPage(currentPage + 1);
        setTimeout(() => {
          setIsLoading(false);
        }, 300);

        dispatch(updateBulkApplyState("secondary-bulk"));
      }
    }

    dispatch(updateSelectedJob({}));
  }

  function handleViewJobApplicationHistory() {
    window.open("/profile#job-application-history/?pending");
  }

  function handleReviewJobsApply() {
    // Display Current Salary Dialog
    const dontShow = localStorage.getItem(
      `currentSalaryDialogDontShow_${user?.id}`
    );
    if (!user?.currentSalary && !dontShow) {
      localStorage.setItem("showCurrentSalaryDialogAfterJump", "1");
    }

    handleCloseDialog();
    if (!router.pathname.includes("/apply")) {
      router.push("/profile#job-application-history/?pending");
    } else {
      router.push("/profile#job-application-history/?pending");
    }
  }

  useEffect(() => {
    if (jobGlobalId) {
      const params = {
        globalId: jobGlobalId,
        first: 30,
      };

      setCurrentPage(0);
      dispatch(getPostApplyRecommendedJobs(params));
    }
  }, [jobGlobalId]);

  return (
    <DialogContainer
      $bulkApplyState={bulkApplyState}
      open={showDialog}
      onClose={handleCloseAndRedirect}
    >
      {!similarJobsTriggered && (
        <DialogHeader>
          <BoxContainer alignItems={"center"} justifyContent={"space-between"}>
            <BoxContainer gap={"0.5rem"} alignItems={"center"}>
              {DialogHeaderIcon}
              <TextStyled
                fontSize={isMobile ? "0.875rem" : "1.25rem"}
                fontWeight={"700"}
                lineHeight={"24px"}
              >
                {DialogHeaderTitle}
              </TextStyled>
            </BoxContainer>
            <CloseButton onClick={handleCloseAndRedirect}>
              <CloseIcon />
            </CloseButton>
          </BoxContainer>
          <ContentText>{DialogHeaderSubtitle} </ContentText>
        </DialogHeader>
      )}

      <DialogContent $similarJobsTriggered={similarJobsTriggered}>
        {bulkApplyState === "post-apply" ? (
          <BulkApplyList
            dialog={true}
            secondaryBulk={bulkApplyState === "secondary-bulk"}
            selectedJobs={selectedJobs}
            setSelectedJobs={setSelectedJobs}
            fetchingJobs={isLoadingJobs}
            recommendedJobs={recommendedJobs}
            jobs={filteredJobs}
            jobListRef={jobListRef}
            currentTab={selectedTab}
            currentPage={currentPage}
          />
        ) : bulkApplyState === "secondary-bulk" ? (
          <SecondaryBulkApply
            dialog={true}
            showList={showList}
            setShowList={setShowList}
            selectedJobs={selectedJobs}
            setSelectedJobs={setSelectedJobs}
            fetchingJobs={isLoadingJobs}
            recommendedJobs={recommendedJobs}
            filteredJobs={filteredJobs}
            jobListRef={jobListRef}
            currentTab={selectedTab}
          />
        ) : bulkApplyJobsStatus ? (
          <AppliedJobWrapper>
            <AppliedJobContainer>
              <HeaderStyled>
                Here are the job(s) you've applied to:
              </HeaderStyled>
              <AppliedJobList jobs={bulkAppliedJobsTotal} />
            </AppliedJobContainer>
            <MobileAppAd />
          </AppliedJobWrapper>
        ) : null}
      </DialogContent>

      {origin == "Applied" ? (
        <Grid>
          {bulkApplyJobsStatus ? (
            <DialogFooter status={bulkApplyJobsStatus}>
              <AppliedButtonStyled onClick={handleReviewJobsApply}>
                Review Application
              </AppliedButtonStyled>
              <AppliedButtonStyled
                onClick={handleCloseAndRedirect}
                variant={"filled"}
                black
              >
                Done
              </AppliedButtonStyled>
            </DialogFooter>
          ) : (
            <DialogFooter origin={origin} status={bulkApplyJobsStatus}>
              <AppliedButtonStyled
                variant={"outlined"}
                onClick={handleViewAllJobs}
              >
                View All Jobs
              </AppliedButtonStyled>
              {filteredJobs.length > 0 && (
                <AppliedButtonStyled
                  onClick={() => handleApplyBulkJob(selectedJobs)}
                  variant={"filled"}
                  disabled={disableApplyButton || isLoadingJobs}
                >
                  {ApplyButtonText}
                </AppliedButtonStyled>
              )}
            </DialogFooter>
          )}
        </Grid>
      ) : (
        <Grid>
          <DialogFooter>
            {bulkApplyJobsStatus ? (
              <>
                <ButtonStyled onClick={handleReviewJobsApply}>
                  Review Application
                </ButtonStyled>
                <ButtonStyled
                  onClick={handleCloseAndRedirect}
                  variant={"filled"}
                >
                  Done
                </ButtonStyled>
              </>
            ) : (
              <>
                {similarJobsTriggered ? (
                  <ButtonStyled
                    onClick={handleCloseDialog}
                    variant={"outlined"}
                  >
                    Back
                  </ButtonStyled>
                ) : bulkApplyState === "post-apply" ||
                  bulkApplyState === "secondary-bulk" ? (
                  <ButtonStyled
                    onClick={handleViewAllJobs}
                    variant={"outlined"}
                  >
                    View All Jobs
                  </ButtonStyled>
                ) : null}

                {filteredJobs.length > 0 && (
                  <ButtonStyled
                    onClick={() => handleApplyBulkJob(selectedJobs)}
                    variant={"filled"}
                    color={"primary"}
                    disabled={disableApplyButton || isLoadingJobs}
                  >
                    {ApplyButtonText}
                  </ButtonStyled>
                )}
              </>
            )}
          </DialogFooter>
        </Grid>
      )}
    </DialogContainer>
  );
}
